import { generateProposalDocument } from "../../services/documentGeneration.service";

import AdvancedOptionsToggle from "./advancedOptionsToggle";
import CustomerDetails from "./customerDetails";
import FormatSelector from "./formatSelector";
import PreviewModal from "./previewModal";
import { useProposalDocument } from "./proposalDocumentsContext";
import IntroForm from "./proposalIntro";
import Quotes from "./quotes/quotes";
import QuotesOutlineRail from "./quotes/quotesOutlineRail";
import SLAContent from "./slaContent";
import Testimonials from "./testimonials";
import ThemePicker from "./themePicker";
import ZohoCustomerValidator from "./zohoCustomerValidator";

interface HtmlGenerationResponse {
  raw_html: string;
}

interface PdfGenerationResponse {
  doc_id: string;
}

// FastAPI error body - `detail` is a list of field errors for a 422, or a plain message otherwise
interface GenerationErrorResponse {
  detail?: string | { loc: (string | number)[]; msg: string }[];
}

const ProposalDocumentForm: React.FC = () => {
  // Context
  const {
    setIsLoading,
    payload,
    setIframeUrl,
    setShowModal,
    pdfJobSubmitted,
    setPdfJobSubmitted,
    isLoading,
    errorMessage,
    setErrorMessage,
    resetForm,
    advancedOptionsEnabled,
    zohoIdValid,
    metersFetched,
    validatePayload,
  } = useProposalDocument();

  const isCustomerVerified = zohoIdValid && metersFetched;

  // Handle form submission (maybe this belongs in a service idk)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    if (!isCustomerVerified) {
      setErrorMessage("No valid Zoho ID supplied");
      return;
    }

    const valid = validatePayload();
    // Validate payload sets error message by default
    if (!valid) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await generateProposalDocument(payload);

      // Pass failures onto error handling
      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        handleGenerationError(res.status, data);
        return;
      }

      if (payload.format === "html") {
        handleHTMLPayload((await res.json()) as HtmlGenerationResponse);
      } else {
        handlePDFPayload((await res.json()) as PdfGenerationResponse);
      }
    } catch (err) {
      console.error("Generation failed:", err);
      handleGenerationError(null, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerationError = (status: number | null, data: unknown) => {
    // Pretty much copied from sales docs since they use same backend except no 404
    switch (status) {
      case 500:
        setErrorMessage("Server Error (500): Please report this to the dev team.");
        break;
      case 403:
        setErrorMessage(
          "You do not have the correct permissions to generate documents, contact the dev team to get your permissions updated.",
        );
        break;
      case 422: {
        const details422 = (data as GenerationErrorResponse | null)?.detail;
        if (Array.isArray(details422)) {
          const msg = details422.map((d) => `${String(d.loc.at(-1))}: ${d.msg}`).join(", ");
          setErrorMessage(`Validation Error: ${msg}`);
        } else {
          setErrorMessage(details422 ?? "Invalid input data.");
        }
        break;
      }
      default:
        setErrorMessage("An unexpected error occurred. Please check your connection.");
    }
  };

  const handleHTMLPayload = (data: HtmlGenerationResponse) => {
    const html = data.raw_html;

    // Create the blob containing the raw HTML string
    const blob = new Blob([html], { type: "text/html" });

    // Generate the temporary local URL for the iframe
    const url = URL.createObjectURL(blob);

    setIframeUrl(url);
    setShowModal(true);
    setIsLoading(false);
  };

  const handlePDFPayload = (data: PdfGenerationResponse) => {
    // If there isn't a doc_id included i.e. wasn't successful, it'll propogate up an error
    const docId = data.doc_id;
    console.log("we got the doc_id!", docId);
    setPdfJobSubmitted(true);
  };

  // The page
  return (
    <div className="proposal-document-form">
      <h1>Generate Proposal Document</h1>

      <div className="proposal-document-form__layout">
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="proposal-form"
        >
          <ZohoCustomerValidator />

          {isCustomerVerified && (
            <>
              <AdvancedOptionsToggle />
              <ThemePicker />
              <CustomerDetails />
              {advancedOptionsEnabled && <IntroForm />}
              {advancedOptionsEnabled && <Testimonials />}
              <Quotes />
              <SLAContent />
              <FormatSelector />
              <PreviewModal />

              {/* Submit Section */}
              <div className="proposal-form__submit-section">
                <button type="submit" className="proposal-form__submit-button" disabled={isLoading}>
                  {isLoading ? "Preparing your document..." : "Generate Document"}
                </button>

                {/* Error Messages */}
                {errorMessage && (
                  <div className="proposal-form__error-alert" role="alert">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <pre>{errorMessage}</pre>
                      <button
                        type="button"
                        className="error-close-button"
                        onClick={() => {
                          setErrorMessage("");
                        }}
                        aria-label="Close error message"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {/* Loading State */}
                <div className="proposal-form__loading-state">
                  {isLoading && <div className="proposal-form__spinner" aria-hidden="true" />}
                  {pdfJobSubmitted && (
                    <div className="proposal-form__loading-message">
                      <p>
                        Your PDF is being rendered and may take several minutes, we&apos;ll email it
                        once completed!
                      </p>
                      <p className="muted-text">
                        This may go to junk - add <code>noreply@geab-phoenix.com</code> to your safe
                        senders.
                      </p>
                      <p className="muted-text">
                        You may safely navigate away from this page or create more proposals.
                      </p>
                      <button
                        type="button"
                        className="proposal-form__reset-button"
                        onClick={resetForm}
                      >
                        Start new proposal
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </form>
        {isCustomerVerified && <QuotesOutlineRail />}
      </div>
    </div>
  );
};

export default ProposalDocumentForm;
